import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PortalNav } from "@/components/PortalNav";
import { UserCog, Plus, Trash2, Loader2, ShieldCheck, Copy, X, KeyRound } from "lucide-react";
import { ResetPasswordModal } from "@/components/ResetPasswordModal";

const ROLE_LABEL = { admin: "Admin", manager: "Manager", sales_manager: "Sales Manager", sales: "Sales" };
const ROLE_STYLE = {
  admin: "bg-[#0F172A] text-white",
  manager: "bg-[#2563EB] text-white",
  sales_manager: "bg-[#0D9488] text-white",
  sales: "bg-[#D4AF37] text-[#0F172A]",
};

export default function UsersAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const roleOptions = isAdmin ? ["sales", "sales_manager", "manager", "admin"] : ["sales"];

  const [users, setUsers] = useState([]);
  const [meId, setMeId] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", role: "sales", password: "" });
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null);
  const [resetFor, setResetFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
      setMeId(data.me?.id || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", { ...form, email: form.email.trim().toLowerCase() });
      setCreds({ ...data.credentials, name: form.name, role: form.role });
      toast.success(`${ROLE_LABEL[form.role]} account created`);
      setForm({ name: "", email: "", role: "sales", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}? This deletes their login.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove user");
    }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    if (!window.confirm(`Change ${u.name || u.email} to ${ROLE_LABEL[role]}? This updates what they can access.`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.name || u.email} is now a ${ROLE_LABEL[role]}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change role");
    }
  };

  const canManage = (u) => u.id !== meId && (isAdmin || u.role === "sales");

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-1"><UserCog className="h-5 w-5 text-[#D4AF37]" /><span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Admin</span></div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A] mb-8">Users</h1>

        <form onSubmit={create} className="bg-white border border-zinc-200 p-5 mb-8 grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="user-create-form">
          <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="user-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="user-email" />
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
              {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </div>
          <Field label="Password (optional)" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="user-password" placeholder="Auto-generated" />
          <button type="submit" disabled={saving} data-testid="user-create-submit" className="flex items-center justify-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add User</>}
          </button>
          {!isAdmin && <p className="md:col-span-5 text-xs text-zinc-500">As a Manager you can add Sales users and Technicians. Only an Admin can add Admins or Managers.</p>}
        </form>

        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
            <h2 className="font-display font-bold text-[#0F172A]">Staff accounts</h2>
            <span className="text-xs text-zinc-400">({users.length})</span>
          </div>
          {loading ? (
            <div className="p-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-zinc-400 text-sm">No staff accounts yet.</div>
          ) : (
            <div className="divide-y divide-zinc-100" data-testid="users-list">
              {users.map((u) => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium text-[#0F172A] truncate">{u.name || "—"}{u.id === meId && <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-400">(you)</span>}</div>
                    <div className="text-xs text-zinc-400 font-mono truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {isAdmin && u.id !== meId ? (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        data-testid={`user-role-select-${u.id}`}
                        className="text-xs border border-zinc-300 bg-white px-2 py-1 outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      >
                        {["sales", "sales_manager", "manager", "admin"].map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : (
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 ${ROLE_STYLE[u.role] || "bg-zinc-200 text-zinc-600"}`}>{ROLE_LABEL[u.role] || u.role}</span>
                    )}
                    {canManage(u) && <button onClick={() => setResetFor(u)} title="Change password" data-testid={`user-reset-pw-${u.id}`} className="text-zinc-400 hover:text-[#D4AF37] transition-colors"><KeyRound className="h-4 w-4" /></button>}
                    {canManage(u) && <button onClick={() => remove(u)} data-testid={`user-del-${u.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {resetFor && <ResetPasswordModal user={resetFor} onClose={() => setResetFor(null)}
        onDone={(d) => { setResetFor(null); setCreds({ ...d, name: resetFor.name || resetFor.email, role: resetFor.role, reset: true }); }} />}
      {creds && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center px-4" onClick={() => setCreds(null)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="user-creds-modal">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="font-display font-bold text-[#0F172A]">{creds.reset ? "Password changed" : `${ROLE_LABEL[creds.role]} login created`}</h3>
              <button onClick={() => setCreds(null)} className="text-zinc-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <p className="text-zinc-600">Share these credentials with <b>{creds.name}</b>. This password won't be shown again.{creds.reset && (creds.emailed ? " A copy was also emailed to them." : " (Email copy was not sent — emails may be turned off.)")}</p>
              <div className="bg-zinc-50 border border-zinc-200 p-3 font-mono text-xs space-y-1">
                <div><span className="text-zinc-400">Email:</span> {creds.email}</div>
                <div><span className="text-zinc-400">Password:</span> {creds.password}</div>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`); toast.success("Copied"); }} className="flex items-center gap-2 text-sm text-[#0F172A] hover:text-[#D4AF37]"><Copy className="h-4 w-4" /> Copy</button>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end">
              <button onClick={() => setCreds(null)} data-testid="user-creds-done" className="bg-[#0F172A] text-white px-5 py-2 text-sm font-medium hover:bg-[#1E293B]">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
    </div>
  );
}
